<template>
  <div :class="computedRootClasses">
    <TOverlay
      :show="isLoading"
      rounded
      blur
      class="flex grow flex-col"
    >
      <div :class="computedClasses">
        <TCardHeader
          v-if="hasHeader"
          :title="title"
          :subtitle="subtitle"
        >
          <template
            v-if="slots?.title"
            #title
          >
            <slot name="title" />
          </template>
          <template
            v-if="slots?.subtitle"
            #subtitle
          >
            <slot name="subtitle" />
          </template>
          <template
            v-if="slots?.actions"
            #actions
          >
            <slot name="actions" />
          </template>
        </TCardHeader>
        <slot
          v-else
          name="header"
        />

        <div
          v-if="slots?.default"
          class="grow"
        >
          <slot />
        </div>

        <div v-if="slots?.footer">
          <slot name="footer" />
        </div>
      </div>
    </TOverlay>

    <!-- This slot can be used for content that doesn't directly render in the card, like modals. -->
    <slot name="additional" />
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  /**
   * Renders the card without a border.
   */
  isBorderless?: boolean

  /**
   * Renders the card with no padding.
   */
  isCompact?: boolean

  /**
   * Renders borders between the header, content, and footer sections.
   */
  isSeparated?: boolean

  /**
   * Renders a loading spinner in the center of the card.
   */
  isLoading?: boolean

  /**
   * The title of the card.
   */
  title?: string

  /**
   * The subtitle of the card.
   */
  subtitle?: string

  /**
   * Sets the card as active.
   */
  isActive?: boolean
}>()

const computedRootClasses = computed(() => [
  'bg-white relative flex flex-col @container/card',
  { 'border-0 rounded-none': props.isBorderless },
  { 'border border-border-default rounded-lg': !props.isBorderless },
  { 'outline-2 outline-secondary -outline-offset-2': !props.isBorderless && props.isActive },
])
const computedClasses = computed(() => [
  'flex grow flex-col',
  { 'gap-4': !props.isSeparated },
  { 'p-4 @min-xl/card:p-6': !props.isCompact && !props.isSeparated },
  { 'divide-y divide-border-default': props.isSeparated },
  { '*:p-4 @min-xl/card:*:p-6': !props.isCompact && props.isSeparated },
  { '*:py-4 @min-xl/card:*:py-6 *:first:pt-0 *:last:pb-0': props.isCompact && props.isSeparated },
])

const slots = useSlots()
const hasHeader = computed(() => {
  return props.title || props.subtitle || slots.title || slots.subtitle || slots.actions
})
</script>
